import { Component, OnInit } from '@angular/core';
import { AuditService } from './audit.service';
import { SnackbarService } from 'src/app/shared/services/snackbar.service';
import { CurrentUserService } from 'src/app/shared/services/current-user.service';
import { AuditLog } from 'src/app/shared/models/AuditLog';
import { AuditLogPage } from 'src/app/shared/models/AuditLogPage';
import { UserProfile } from 'src/app/shared/models/user-profile';
import { userAction } from 'src/app/shared/models/user-action';
import { AuthenticationService } from 'src/app/shared/services/authentication.service';
import { AUDIT_FIELD_LABELS, AUDIT_EXCLUDED_FIELDS } from './audit-field-labels';
import { AuditDetailDialogComponent } from './audit-detail-dialog/audit-detail-dialog.component';
import { MatDialog } from '@angular/material/dialog';

@Component({
  selector: 'app-audit-management',
  templateUrl: './audit-management.component.html',
  styleUrl: './audit-management.component.less'
})
export class AuditManagementComponent implements OnInit {

  constructor(
    private auditService: AuditService,
    private snackbarService: SnackbarService,
    private authenticationService: AuthenticationService,
    private currentUserService: CurrentUserService,
    private dialog: MatDialog
  ) {}

  public UserProfile = UserProfile;
  public userAction = userAction;

  displayedColumns = [
    'entityNumber', 'entityType', 'operation', 'user',
    'changes', 'motifRetourArriere', 'motifRefus', 'createdAt'
  ];

    readonly entityTypeOptions = [
      { value: '', label: 'Tous' },
      { value: 'DEVIS', label: 'Devis' },
      { value: 'DEMAND', label: 'Demande' }
  ];
  
  readonly operationOptions = [
      { value: '', label: 'Tous' },
      { value: userAction.CREATE, label: 'Création' },
      { value: userAction.UPDATE, label: 'Modification' },
      { value: userAction.DELETE, label: 'Suppression' }
  ];
  
  readonly roleOptions = [
      { value: '', label: 'Tous' },
      { value: UserProfile.ADMIN, label: 'Administrateur' },
      { value: UserProfile.CP_CDS, label: 'Acteur CDS' },
      { value: UserProfile.CP_SNCF, label: 'Acteur SNCF' },
      { value: UserProfile.SERVICE_MANAGER, label: 'Service Manager' },
      { value: UserProfile.MANAGER_CDS, label: 'Manager CDS' },
      { value: UserProfile.GUEST_CDS, label: 'Guest CDS' },
      { value: UserProfile.GUEST_SNCF, label: 'Guest SNCF' }
  ];  

  fiches: AuditLog[] = [];
  fichesFiltrees: AuditLog[] = [];
  filterValue = '';

  totalElements = 0;
  pageSize = 10;
  pageIndex = 0;

  filters: {
    entityType: string;
    operation: string;
    role: string;
    dateFrom: Date | null;
    dateTo: Date | null;
  } = {
    entityType: '',
    operation: '',
    role: '',
    dateFrom: null,
    dateTo: null
  };

  ngOnInit() {
    this.loadAudit();
  }

  loadAudit() {
    const connectedUser = this.currentUserService.getCurrentUser();
    const isAdminOrManager = this.authenticationService.isUserAdminOrServiceManagerOrManagerCDS();

    if (!isAdminOrManager) {
        const applications = (connectedUser?.applications || []).map((app: any) => 
    typeof app === 'string' ? app : app.name
);

        const body: any = {
    page: this.pageIndex,
    size: this.pageSize,
    applications: applications,
    entityType: this.filters.entityType ? [this.filters.entityType] : [],
    operation: this.filters.operation ? [this.filters.operation] : [],
    actorRole: this.filters.role ? [this.filters.role] : [],
    dateRange: (this.filters.dateFrom || this.filters.dateTo) ? {
    from: this.filters.dateFrom
        ? new Date(this.filters.dateFrom).toISOString()
        : null,
    to: this.filters.dateTo
        ? new Date(new Date(this.filters.dateTo).setHours(23, 59, 59, 999)).toISOString()
        : null
} : null,
    sort: [{ field: 'createdAt', direction: 'DESC' }]
};

        this.auditService.getAuditLogsByApplications(body).subscribe({
            next: (res: AuditLogPage) => {
                this.fiches = res.content;
                this.totalElements = res.totalElements;
                this.applyLocalFilter();
            },
            error: () => {
                this.snackbarService.openSnackBar(
                    "Erreur lors du chargement des logs d'audit",
                    'error-snack-bar'
                );
            }
        });

    } else {
        const params: any = {
            page: this.pageIndex,
            size: this.pageSize,
            entityType: this.filters.entityType || null,
            operation: this.filters.operation || null,
            role: this.filters.role || null,
            dateFromISO: this.filters.dateFrom,
            dateToISO: this.filters.dateTo
        };

        this.auditService.getAllAuditLogs(params).subscribe({
            next: (res: AuditLogPage) => {
                this.fiches = res.content;
                this.totalElements = res.totalElements;
                this.applyLocalFilter();
            },
            error: () => {
                this.snackbarService.openSnackBar(
                    "Erreur lors du chargement des logs d'audit",
                    'error-snack-bar'
                );
            }
        });
    }
}

  applyFilter(event: Event): void {
    this.filterValue = (event.target as HTMLInputElement).value.trim().toLowerCase();
    this.applyLocalFilter();
  }

  private applyLocalFilter(): void {
  if (!this.filterValue) {
    this.fichesFiltrees = [...this.fiches];
    return;
  }

  this.fichesFiltrees = this.fiches.filter(element => {
    const changes = this.getChangedFieldsMap(element.changedFields, element.entityType)
      .map(c => `${c.label} ${c.before} ${c.after}`)
      .join(' ');

    const searchStr = [
      this.getEntityNumber(element),                          // n° devis OU demande
      element.entityType,
      element.operation,
      this.getActionLabel(element.operation),                 // "Création", "Modification"...
      element.actorDisplayName,
      element.actorRole,
      changes,                                                // changements affichés
      this.getMotifRetourArriere(element.changedFields),
      this.getMotifRefus(element.changedFields),
      element.createdAt
        ? new Date(element.createdAt).toLocaleString('fr-FR') // date au format affiché
        : ''
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    return searchStr.includes(this.filterValue);
  });
}



  // ── Helpers changedFields ─────────────────────────────────────────────
  // changedFields arrive sous la forme : { items: [{field, before, after}] }

  private getChangedItems(changedFields: any): any[] {
    if (!changedFields) return [];
    
    // Cas tableau wrappé { items: [...] }
    if (changedFields.items && Array.isArray(changedFields.items)) {
      return changedFields.items;
    }
    
    // Cas objet direct {devisStatut: {before, after}, ...}
    return Object.keys(changedFields).map(field => ({
      field: field,
      before: changedFields[field].before,
      after: changedFields[field].after
    }));
  }

  private findField(changedFields: any, fieldName: string): any {
    return this.getChangedItems(changedFields).find(
      (item: any) => item.field === fieldName
    ) || null;
  }

  getStatusChange(changedFields: any) {
    const item = this.findField(changedFields, 'devisStatut');
    if (!item) return null;
    return { before: item.before, after: item.after };
  }

  getMotifRetourArriere(changedFields: any): string | null {
    const item = this.findField(changedFields, 'devisMotifRetourArriere');
    return item ? item.after : null;
  }

  getMotifRefus(changedFields: any): string | null {
    const item = this.findField(changedFields, 'devisMotifRefus');
    return item ? item.after : null;
  }

  formatChanges(changedFields: any): string[] {
    return this.getChangedItems(changedFields).map((item: any) => {
      const before = item.before ?? '-';
      const after = item.after ?? '-';
      return `${item.field} : ${before} → ${after}`;
    });
  }

  // ── Autres helpers ────────────────────────────────────────────────────

  getLabel(key: string): string {
    const labels: any = {
      devisStatut: 'Statut',
      devisMotifRefus: 'Motif de refus',
      devisMotifRetourArriere: 'Motif retour arrière'
    };
    return labels[key] || key;
  }

  getEntityNumber(element: AuditLog): any {
    const isCreate = element.operation === 'CREATE';

    const source = isCreate ? element.snapshotAfter : element.snapshotBefore;

    switch (element.entityType) {
      case 'DEVIS':
        return source?.devisReference;

      case 'DEMAND':
        return source?.demandNumber;

      case 'FEB':
        return source?.febNumber;

      default:
        return null;
    }
}

  getActionLabel(operation: string): string {
    const labels: any = {
      CREATE: 'Création',
      UPDATE: 'Modification',
      DELETE: 'Suppression'
    };
    return labels[operation] || operation;
  }


getChangedFieldsMap(changedFields: any, entityType: string)
    : { field: string; label: string; before: any; after: any }[] {

  if (!changedFields) return [];
  const labelsForEntity = AUDIT_FIELD_LABELS[entityType?.toUpperCase()] ?? {};

  const entries = Array.isArray(changedFields)
      ? changedFields.map(item => ({ field: item.field, before: item.before, after: item.after }))
      : Object.entries(changedFields).map(([field, value]: [string, any]) => ({
            field, before: value.before, after: value.after
        }));

  const result: { field: string; label: string; before: any; after: any }[] = [];
  const echeanceChanges: { before: any; after: any }[] = [];

  for (const item of entries) {
    if (AUDIT_EXCLUDED_FIELDS.includes(item.field)) continue;

    // Champs d'échéances → mis de côté pour regroupement
    if (item.field.startsWith('echeances[')) {
      if (item.field.endsWith('.status')) {
        echeanceChanges.push({ before: item.before, after: item.after });
      }
      continue;  // on ignore les autres sous-champs d'échéance
    }

    result.push({
      field: item.field,
      label: labelsForEntity[item.field] ?? item.field,
      before: this.formatValue(item.before),
      after: this.formatValue(item.after)
    });
  }

  // Regroupement : si toutes les échéances ont la même transition → une seule ligne
  if (echeanceChanges.length > 0) {
    const uniqueTransitions = new Set(
      echeanceChanges.map(c => `${c.before}→${c.after}`)
    );
    if (uniqueTransitions.size === 1) {
      result.push({
        field: 'echeances',
        label: `Statut échéances (×${echeanceChanges.length})`,
        before: this.formatValue(echeanceChanges[0].before),
        after: this.formatValue(echeanceChanges[0].after)
      });
    } else {
      echeanceChanges.forEach((c, i) => result.push({
        field: `echeance_${i}`,
        label: `Statut échéance ${i + 1}`,
        before: this.formatValue(c.before),
        after: this.formatValue(c.after)
      }));
    }
  }

  return result;
}

openAuditDetail(element: AuditLog): void {
  this.dialog.open(AuditDetailDialogComponent, {
    data: {
      audit: element,
      changes: this.getChangedFieldsMapDetailed(element.changedFields, element.entityType)
    },
    width: '700px',
    maxWidth: '95vw',
    autoFocus: false
  });
}

getChangedFieldsMapDetailed(
    changedFields: any,
    entityType: string
): { field: string; label: string; before: any; after: any }[] {

  if (!changedFields) return [];
  const labelsForEntity = AUDIT_FIELD_LABELS[entityType?.toUpperCase()] ?? {};

  const entries = Array.isArray(changedFields)
      ? changedFields.map(item => ({ field: item.field, before: item.before, after: item.after }))
      : Object.entries(changedFields).map(([field, value]: [string, any]) => ({
            field, before: value.before, after: value.after
        }));

  let echeanceCounter = 0;

  return entries
      .filter(item => !AUDIT_EXCLUDED_FIELDS.includes(item.field))
      .map(item => {
        let label: string;
        if (item.field.startsWith('echeances[')) {
          echeanceCounter++;
          label = `Statut échéance ${echeanceCounter}`;
        } else {
          label = labelsForEntity[item.field] ?? item.field;
        }
        return {
          field: item.field,
          label,
          before: this.formatValue(item.before),
          after: this.formatValue(item.after)
        };
      });
}

private formatValue(value: any): string {
    if (value === null || value === undefined) return '—';
    
    //  détecte toute string qui ressemble à une date ISO
    if (typeof value === 'string') {
        const date = new Date(value);
        if (!isNaN(date.getTime()) && value.includes('-')) {
            return date.toLocaleDateString('fr-FR', {
                day: '2-digit',
                month: '2-digit',
                year: 'numeric'
            });
        }
    }
    
    if (typeof value === 'number') return value.toLocaleString('fr-FR');
    if (typeof value === 'boolean') return value ? 'Oui' : 'Non';
    return String(value);
}


  onPageChange(event: any) {
    const pageSizeChanged = this.pageSize !== event.pageSize;
    this.pageSize = event.pageSize;
    this.pageIndex = pageSizeChanged ? 0 : event.pageIndex;
    this.loadAudit();
  }

  onFilterChange() {
    this.pageIndex = 0;
    this.loadAudit();
  }
}